import { StanLoader, StanModal, StanToggle } from '@/components';
import { AudienceModalProps } from '../../types/audience.types';
import { useAudienceSelection } from '../../hooks/useAudienceSelection';
import { useTagManagement } from '../../queries';
import { combineTags } from '../../logic/audienceBuilder.logic';
import { TagSelector } from './TagSelector';
import { AudienceTable } from './AudienceTable';
import { AudienceEmptyState } from './AudienceEmptyState';

export const AddAudienceModal = ({ isOpen, onClose, onConfirm, initialTags = [] }: AudienceModalProps) => {
    const {
        includeTags,
        setIncludeTags,
        excludeTags,
        setExcludeTags,
        excludeToggle,
        setExcludeToggle,
        fans,
        recipientCount,
        isLoading,
        currentPage,
        setCurrentPage,
        totalPages,
        productOptions,
    } = useAudienceSelection({ initialTags });

    // Tag management for include tags
    const includeTagManagement = useTagManagement({
        mode: 'include',
        selectedTags: includeTags,
        onTagsChange: setIncludeTags,
        productOptions,
    });

    // Tag management for exclude tags
    const excludeTagManagement = useTagManagement({
        mode: 'exclude',
        selectedTags: excludeTags,
        onTagsChange: setExcludeTags,
        productOptions,
    });

    const handleConfirm = () => {
        const combinedTags = combineTags(includeTags, excludeTags);
        onConfirm(combinedTags, recipientCount);
        onClose();
    };

    const handleExcludeToggleChange = (checked: boolean) => {
        setExcludeToggle(checked);
        if (!checked) {
            setExcludeTags([]);
        }
    };

    const hasSelectedTags = includeTags.length > 0 || excludeTags.length > 0;

    return (
        <StanModal
            id='add-audience-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Add Audience'
            size='xl'
            primaryActionLabel='Confirm'
            secondaryActionLabel='Cancel'
            onPrimaryAction={handleConfirm}
            onSecondaryAction={onClose}
            primaryActionDisabled={includeTags.length === 0}
        >
            <div className='flex w-full flex-col gap-4'>
                {/* Tag Selectors */}
                <div className='flex flex-col gap-2'>
                    <TagSelector
                        mode='include'
                        selectedTags={includeTags}
                        availableTags={includeTagManagement.availableTags}
                        onTagAdd={includeTagManagement.handleTagAdd}
                        onTagRemove={includeTagManagement.handleTagRemove}
                        placeholder='Select tags'
                    />

                    {/* Exclude Users Toggle */}
                    <div className='flex items-center gap-2'>
                        <StanToggle
                            id='exclude-users-toggle'
                            checked={excludeToggle}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleExcludeToggleChange(e.target.checked)
                            }
                            toggleSize='sm'
                        />
                        <label className='text-dark text-label-xs-regular font-medium'>Exclude Users</label>
                    </div>

                    {excludeToggle && (
                        <TagSelector
                            mode='exclude'
                            selectedTags={excludeTags}
                            availableTags={excludeTagManagement.availableTags}
                            onTagAdd={excludeTagManagement.handleTagAdd}
                            onTagRemove={excludeTagManagement.handleTagRemove}
                            placeholder="Don't send to..."
                        />
                    )}
                </div>

                {/* Table, Loading State, or Empty State */}
                <div className='flex flex-col gap-4 transition-all duration-300'>
                    {isLoading ? (
                        <div className='flex min-h-72 items-center justify-center'>
                            <StanLoader />
                        </div>
                    ) : fans.length > 0 ? (
                        <AudienceTable
                            fans={fans}
                            recipientCount={recipientCount}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            isLoading={false}
                            onPageChange={setCurrentPage}
                        />
                    ) : (
                        <AudienceEmptyState hasSelectedTags={hasSelectedTags} />
                    )}
                </div>
            </div>
        </StanModal>
    );
};
